import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import {Provider} from "react-redux";
import store, {persistor} from './redux/store';
import { PersistGate } from 'redux-persist/integration/react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { registerPWA, handlePWALifecycle } from './pwa';

// Import Tailwind CSS
import './app.css';

// Public Components
import Landing from './components/pages/Landing/Landing';
import SignUpPage from './components/pages/SignUpPage';
import SignInPage from './components/pages/SignInPage';
import ResetPassPage from './components/pages/ResetPassPage';
import TermsOfService from './components/pages/TOS';
import PrivacyPolicy from './components/pages/Privacy';

// Auth Protected Components 
import withAuthProtection from './components/HOCs/AuthProtected';
import ErrorBoundary from './components/ErrorBoundary';

import GameAnalyzer from './components/analytics/GameAnalyzer';
import MySystems from './components/pages/MySystems';
import Profile from './components/pages/Profile';
import Subscribed from './components/pages/Subscribed';
import System from './components/pages/System/System';
import SystemBuilder from './components/createSystem/SystemBuilder';
import UpcomingGames from './components/pages/UpcomingGames/UpcomingGames';

const ProtectedProfile = withAuthProtection(Profile);
const ProtectedSystemBuilder = withAuthProtection(SystemBuilder);
const ProtectedMySystems = withAuthProtection(MySystems, true);
const ProtectedSystem = withAuthProtection(System);
const ProtectedSubscribed = withAuthProtection(Subscribed);
const ProtectedUpcomingGames = withAuthProtection(UpcomingGames);
const ProtectedGameAnalzer = withAuthProtection(GameAnalyzer);

const stripeKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
const stripePromise = loadStripe(stripeKey);

const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    errorElement: <ErrorBoundary />,
    children: [
      { 
        index: true, 
        element: <Landing />,
        errorElement: <ErrorBoundary />
      },
      { 
        path: "about", 
        element: <Landing />,
        errorElement: <ErrorBoundary />
      },
      { 
        path: "game-analyzer", 
        element: <ProtectedGameAnalzer />,
        errorElement: <ErrorBoundary />
      },
      { 
        path: "resetpassword", 
        element: <ResetPassPage />,
        errorElement: <ErrorBoundary />
      },
      { 
        path: "profile", 
        element: <ProtectedProfile />,
        errorElement: <ErrorBoundary />
      },
      { 
        path: "signin", 
        element: <SignInPage />,
        errorElement: <ErrorBoundary />
      },
      { 
        path: "signup", 
        element: <SignUpPage />,
        errorElement: <ErrorBoundary />
      },
      { 
        path: "subscribed", 
        element: <ProtectedSubscribed />,
        errorElement: <ErrorBoundary />
      },
      { 
        path: "system-builder", 
        element: <ProtectedSystemBuilder />,
        errorElement: <ErrorBoundary />
      },
      { 
        path: "systems", 
        element: <ProtectedMySystems />,
        errorElement: <ErrorBoundary />
      },
      { 
        path: "system/:id", 
        element: <ProtectedSystem />,
        errorElement: <ErrorBoundary />
      },
      { 
        path: "tos", 
        element: <TermsOfService />,
        errorElement: <ErrorBoundary />
      },
      { 
        path: "privacy", 
        element: <PrivacyPolicy />,
        errorElement: <ErrorBoundary />
      },
      { 
        path: "upcoming-games", 
        element: <ProtectedUpcomingGames />,
        errorElement: <ErrorBoundary />
      }
    ]
  }
]);

const root = ReactDOM.createRoot(document.getElementById('root'))

root.render(
  <React.StrictMode>
    <Elements stripe={stripePromise}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <RouterProvider router={router} />
        </PersistGate>
      </Provider>
    </Elements>
  </React.StrictMode>
);

// Register PWA service worker and lifecycle handlers
registerPWA();
handlePWALifecycle();
